import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import { GUEST_ROOM_CHAT, pathNames } from '@/shared/utils/constants'
import { resolvePrepareToCallRoomId } from '@/shared/utils/router'
import { toPServedUser } from '@/shared/utils/uri'

import { setPrepareToCallPropsResolver } from '../lazy-route-props'

const isGuestPrepareToCallRoute = (to: RouteLocationNormalized): boolean => {
  return to.name === pathNames.GUEST_PREPARE_TO_CALL
}

export const beforeEnterPrepareToCall = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const [
    { useStoreRooms },
    { isCurrentUserRoomMember },
    { prepareToCallPropsFn },
  ] = await Promise.all([
    import('@/entities/room'),
    import('@/features/join-to-chat'),
    import('../props-fn/prepare-to-call-props-fn'),
  ])
  setPrepareToCallPropsResolver(prepareToCallPropsFn)

  const roomId = resolvePrepareToCallRoomId(to.params.roomId as string)
  const storeRooms = useStoreRooms()

  const getRoomFromStore = () => (
    storeRooms.getRoomById(roomId) ?? storeRooms.findByPServed(toPServedUser(roomId, true))
  )

  if (!to.meta) {
    to.meta = {}
  }

  const tryProceedAsMember = (): boolean => {
    if (isCurrentUserRoomMember(getRoomFromStore())) {
      to.meta.prepareToCallProps = prepareToCallPropsFn(to)
      next()
      return true
    }
    return false
  }

  if (tryProceedAsMember()) {
    return
  }

  try {
    await storeRooms.awaitForRoomAdded(roomId, 500)
  } catch {
    // room not added yet — page will handle access check
  }

  if (tryProceedAsMember()) {
    return
  }

  if (isGuestPrepareToCallRoute(to)) {
    next({ name: GUEST_ROOM_CHAT })
    return
  }

  if (!getRoomFromStore()) {
    try {
      storeRooms.createEmptyGroupRoom(toPServedUser(roomId, true))
    } catch (error: unknown) {
      console.warn(error)
    }
  }

  to.meta.prepareToCallProps = prepareToCallPropsFn(to)
  next()
}
